import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Settings, 
  User, 
  Lock, 
  Plus, 
  Trash2, 
  Edit2,
  Check,
  X,
  Eye,
  EyeOff,
  Shield,
  Mail,
  Save,
  CreditCard,
  Banknote,
  ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSettingsPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const currentUserId = localStorage.getItem("adminUserId");
  
  // Email settings
  const [emailSettings, setEmailSettings] = useState({
    resend_configured: false,
    sender_email: "",
    admin_email: ""
  });
  const [adminEmail, setAdminEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  
  // Stripe settings
  const [stripeSettings, setStripeSettings] = useState({
    configured: false,
    mode: "not_configured",
    api_key_hint: ""
  });
  const [stripeKey, setStripeKey] = useState("");
  const [savingStripe, setSavingStripe] = useState(false);
  
  // Interac settings
  const [interacSettings, setInteracSettings] = useState({
    enabled: false,
    email: "",
    security_question: "",
    security_answer: "",
    checkout_instructions: ""
  });
  const [savingInterac, setSavingInterac] = useState(false);
  
  // Deposit settings
  const [depositSettings, setDepositSettings] = useState({
    deposit_percentage: 60,
    enabled: true
  });
  const [savingDeposit, setSavingDeposit] = useState(false);
  
  // Email templates
  const [emailTemplates, setEmailTemplates] = useState({
    event_welcome: { subject: "", body: "", enabled: true },
    interac_instructions: { subject: "", body: "", enabled: true }
  });
  const [savingTemplate, setSavingTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState("settings"); // "settings" or "emails"

  useEffect(() => {
    fetchUsers();
    fetchEmailSettings();
    fetchStripeSettings();
    fetchInteracSettings();
    fetchDepositSettings();
    fetchEmailTemplates();
  }, []);

  const fetchStripeSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/stripe`);
      setStripeSettings(response.data);
    } catch (error) {
      console.error("Error fetching Stripe settings:", error);
    }
  };
  
  const fetchInteracSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/interac`);
      setInteracSettings(response.data);
    } catch (error) {
      console.error("Error fetching Interac settings:", error);
    }
  };
  
  const fetchDepositSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/deposit`);
      setDepositSettings(response.data);
    } catch (error) {
      console.error("Error fetching deposit settings:", error);
    }
  };
  
  const fetchEmailTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/email-templates`);
      setEmailTemplates(response.data.templates);
    } catch (error) {
      console.error("Error fetching email templates:", error);
    }
  };
  
  const handleSaveEmailTemplate = async (templateKey) => {
    setSavingTemplate(templateKey);
    try {
      await axios.put(`${API}/admin/settings/email-templates/${templateKey}`, emailTemplates[templateKey]);
      toast.success("Email template saved");
    } catch (error) {
      toast.error("Failed to save email template");
    } finally {
      setSavingTemplate(null);
    }
  };
  
  const updateTemplate = (templateKey, field, value) => {
    setEmailTemplates(prev => ({
      ...prev,
      [templateKey]: {
        ...prev[templateKey],
        [field]: value
      }
    }));
  };
  
  const handleSaveStripeSettings = async () => {
    setSavingStripe(true);
    try {
      await axios.put(`${API}/admin/settings/stripe`, {
        api_key: stripeKey,
        webhook_url: `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/stripe`
      });
      toast.success("Stripe settings saved");
      setStripeKey("");
      fetchStripeSettings();
    } catch (error) {
      toast.error("Failed to save Stripe settings");
    } finally {
      setSavingStripe(false);
    }
  };
  
  const handleSaveInteracSettings = async () => {
    setSavingInterac(true);
    try {
      await axios.put(`${API}/admin/settings/interac`, interacSettings);
      toast.success("Interac settings saved");
    } catch (error) {
      toast.error("Failed to save Interac settings");
    } finally {
      setSavingInterac(false);
    }
  };
  
  const handleSaveDepositSettings = async () => {
    setSavingDeposit(true);
    try {
      await axios.put(`${API}/admin/settings/deposit`, depositSettings);
      toast.success("Deposit settings saved");
    } catch (error) {
      toast.error("Failed to save deposit settings");
    } finally {
      setSavingDeposit(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchEmailSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/email`);
      setEmailSettings(response.data);
      setAdminEmail(response.data.admin_email === "Not configured" ? "" : response.data.admin_email);
    } catch (error) {
      console.error("Error fetching email settings:", error);
    }
  };
  
  const handleSaveAdminEmail = async () => {
    setSavingEmail(true);
    try {
      await axios.put(`${API}/admin/settings/email`, { admin_email: adminEmail });
      toast.success("Admin email updated");
      fetchEmailSettings();
    } catch (error) {
      toast.error("Failed to update email");
    } finally {
      setSavingEmail(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-settings">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-2">
              Settings
            </h1>
            <p className="text-[#A1A1AA]">Manage admin users, payments, and email templates</p>
          </div>
          {activeTab === "settings" && (
            <button
              onClick={() => setShowAddUserModal(true)}
              className="btn-primary flex items-center gap-2"
              data-testid="add-user-btn"
            >
              <Plus className="w-5 h-5" />
              Add Admin User
            </button>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-[rgba(212,175,55,0.15)] pb-4">
          <button
            onClick={() => setActiveTab("settings")}
            className={`px-6 py-3 font-medium transition-all ${
              activeTab === "settings"
                ? "bg-[#D4AF37] text-[#050505]"
                : "border border-[rgba(212,175,55,0.3)] text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#F5F5F0]"
            }`}
            data-testid="tab-settings"
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Settings & Payments
          </button>
          <button
            onClick={() => setActiveTab("emails")}
            className={`px-6 py-3 font-medium transition-all ${
              activeTab === "emails"
                ? "bg-[#D4AF37] text-[#050505]"
                : "border border-[rgba(212,175,55,0.3)] text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#F5F5F0]"
            }`}
            data-testid="tab-emails"
          >
            <Mail className="w-4 h-4 inline mr-2" />
            Email Templates
          </button>
        </div>

        {/* Settings Tab Content */}
        {activeTab === "settings" && (
          <>
        {/* Users List */}
        <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)]">
          <div className="p-6 border-b border-[rgba(212,175,55,0.15)]">
            <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0] flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#D4AF37]" />
              Admin Users
            </h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-20">
              <User className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
              <h3 className="text-xl font-['Playfair_Display'] text-[#F5F5F0] mb-2">No Users Found</h3>
              <p className="text-[#A1A1AA]">Add your first admin user</p>
            </div>
          ) : (
            <div className="divide-y divide-[rgba(212,175,55,0.1)]">
              {users.map((user) => (
                <motion.div
                  key={user.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-6 flex items-center justify-between hover:bg-[rgba(212,175,55,0.02)]"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#D4AF37] flex items-center justify-center">
                      <span className="text-[#050505] font-bold text-lg">
                        {user.username?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-[#F5F5F0] font-medium flex items-center gap-2">
                        {user.username}
                        {user.id === currentUserId && (
                          <span className="px-2 py-0.5 bg-[rgba(212,175,55,0.2)] text-[#D4AF37] text-xs">
                            You
                          </span>
                        )}
                      </p>
                      <p className="text-[#a1a1aa] text-sm capitalize">{user.role}</p>
                      <p className="text-[#a1a1aa] text-xs">Created: {formatDate(user.created_at)}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setSelectedUser(user);
                        setShowUsernameModal(true);
                      }}
                      className="p-2 border border-[rgba(212,175,55,0.3)] text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors"
                      title="Change Username"
                      data-testid={`edit-username-${user.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedUser(user);
                        setShowPasswordModal(true);
                      }}
                      className="p-2 border border-[rgba(212,175,55,0.3)] text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors"
                      title="Change Password"
                      data-testid={`change-password-${user.id}`}
                    >
                      <Lock className="w-4 h-4" />
                    </button>
                    {users.length > 1 && (
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="p-2 border border-[rgba(212,175,55,0.3)] text-[#A1A1AA] hover:text-red-500 hover:border-red-500 transition-colors"
                        title="Delete User"
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Default Credentials Info */}
        <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6">
          <h3 className="text-lg font-['Playfair_Display'] text-[#F5F5F0] mb-4">Default Credentials</h3>
          <div className="bg-[#0F0F0F] p-4 rounded border border-[rgba(212,175,55,0.1)]">
            <p className="text-[#A1A1AA] text-sm mb-2">
              Initial admin credentials (please change after first login):
            </p>
            <div className="flex gap-8">
              <div>
                <span className="text-[#a1a1aa] text-xs">Username:</span>
                <p className="text-[#F5F5F0] font-mono">admin</p>
              </div>
              <div>
                <span className="text-[#a1a1aa] text-xs">Password:</span>
                <p className="text-[#F5F5F0] font-mono">admin123</p>
              </div>
            </div>
          </div>
        </div>

        {/* Email Notifications Settings */}
        <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)]">
          <div className="p-6 border-b border-[rgba(212,175,55,0.15)]">
            <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0] flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#D4AF37]" />
              Email Notifications
            </h2>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Email Status */}
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${emailSettings.resend_configured ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-[#A1A1AA]">
                Email Service: {emailSettings.resend_configured ? 'Configured' : 'Not Configured'}
              </span>
            </div>
            
            {emailSettings.resend_configured && (
              <>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">Sender Email</label>
                    <input
                      type="text"
                      value={emailSettings.sender_email}
                      disabled
                      className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#a1a1aa] p-3"
                    />
                    <p className="text-[#a1a1aa] text-xs mt-1">Configured in environment</p>
                  </div>
                  
                  <div>
                    <label className="form-label">Admin Notification Emails</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={adminEmail}
                        onChange={(e) => setAdminEmail(e.target.value)}
                        placeholder="email1@example.com, email2@example.com"
                        className="flex-1 bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                        data-testid="admin-email-input"
                      />
                      <button
                        onClick={handleSaveAdminEmail}
                        disabled={savingEmail}
                        className="btn-primary px-4 flex items-center gap-2"
                        data-testid="save-email-btn"
                      >
                        {savingEmail ? (
                          <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    <p className="text-[#a1a1aa] text-xs mt-1">Receives booking & contact notifications. Separate multiple emails with commas.</p>
                  </div>
                </div>
                
                <div className="bg-[#0F0F0F] p-4 border border-[rgba(212,175,55,0.1)]">
                  <h4 className="text-[#D4AF37] text-sm font-semibold mb-2">Automatic Emails</h4>
                  <ul className="text-[#A1A1AA] text-sm space-y-1">
                    <li>• Booking confirmation sent to customer after payment</li>
                    <li>• New booking notification sent to admin email</li>
                  </ul>
                </div>
              </>
            )}
            
            {!emailSettings.resend_configured && (
              <div className="bg-[#0F0F0F] p-4 border border-red-900/30">
                <p className="text-[#A1A1AA] text-sm">
                  To enable email notifications, configure the RESEND_API_KEY environment variable.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Stripe Payment Settings */}
        <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)]">
          <div className="p-6 border-b border-[rgba(212,175,55,0.15)]">
            <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0] flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#D4AF37]" />
              Stripe Payments
            </h2>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Stripe Status */}
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${stripeSettings.configured ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-[#A1A1AA]">
                Stripe: {stripeSettings.configured ? `Configured (${stripeSettings.mode} mode)` : 'Not Configured'}
              </span>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="form-label">Stripe API Key</label>
                <input
                  type="password"
                  value={stripeKey}
                  onChange={(e) => setStripeKey(e.target.value)}
                  placeholder={stripeSettings.api_key_hint ? `••••••••${stripeSettings.api_key_hint}` : "sk_live_..."}
                  className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                  data-testid="stripe-key-input"
                />
                <p className="text-[#a1a1aa] text-xs mt-1">
                  Get your key from{" "}
                  <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">
                    Stripe Dashboard <ExternalLink className="w-3 h-3 inline" />
                  </a>
                </p>
              </div>
              
              <div>
                <label className="form-label">Webhook URL</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={`${process.env.REACT_APP_BACKEND_URL}/api/webhooks/stripe`}
                    disabled
                    className="flex-1 bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] text-[#a1a1aa] p-3 text-sm"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/webhooks/stripe`);
                      toast.success("Webhook URL copied");
                    }}
                    className="p-3 border border-[rgba(212,175,55,0.3)] text-[#D4AF37] hover:bg-[#D4AF37]/10"
                  >
                    Copy
                  </button>
                </div>
                <p className="text-[#a1a1aa] text-xs mt-1">Add this URL in Stripe webhook settings</p>
              </div>
            </div>
            
            <button
              onClick={handleSaveStripeSettings}
              disabled={!stripeKey || savingStripe}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
              data-testid="save-stripe-btn"
            >
              {savingStripe ? (
                <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Stripe Settings
            </button>
          </div>
        </div>

        {/* Interac e-Transfer Settings */}
        <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)]">
          <div className="p-6 border-b border-[rgba(212,175,55,0.15)]">
            <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0] flex items-center gap-2">
              <Banknote className="w-5 h-5 text-[#D4AF37]" />
              Interac e-Transfer
            </h2>
          </div>
          
          <div className="p-6 space-y-6">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={interacSettings.enabled}
                  onChange={(e) => setInteracSettings({ ...interacSettings, enabled: e.target.checked })}
                  className="w-5 h-5 rounded border-[#D4AF37] bg-[#0F0F0F] text-[#D4AF37]"
                />
                <span className="text-[#F5F5F0]">Enable Interac e-Transfer as payment option</span>
              </label>
            </div>
            
            {interacSettings.enabled && (
              <div className="space-y-4">
                <div className="bg-[#0F0F0F] p-4 border border-[rgba(212,175,55,0.1)]">
                  <p className="text-[#A1A1AA] text-sm">
                    Customers will be shown your Interac e-Transfer details at checkout. 
                    You'll need to manually verify payments and mark orders as paid.
                  </p>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">Interac Email</label>
                    <input
                      type="email"
                      value={interacSettings.email}
                      onChange={(e) => setInteracSettings({ ...interacSettings, email: e.target.value })}
                      placeholder="payments@mixandmingle.ca"
                      className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                      data-testid="interac-email-input"
                    />
                  </div>
                  <div>
                    <label className="form-label">Security Question</label>
                    <input
                      type="text"
                      value={interacSettings.security_question}
                      onChange={(e) => setInteracSettings({ ...interacSettings, security_question: e.target.value })}
                      placeholder="What is the event name?"
                      className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                      data-testid="interac-question-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="form-label">Security Answer (will be shown to customers)</label>
                  <input
                    type="text"
                    value={interacSettings.security_answer}
                    onChange={(e) => setInteracSettings({ ...interacSettings, security_answer: e.target.value })}
                    placeholder="mixmingle"
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    data-testid="interac-answer-input"
                  />
                </div>
                
                <div>
                  <label className="form-label">Checkout Instructions (shown at checkout)</label>
                  <textarea
                    value={interacSettings.checkout_instructions}
                    onChange={(e) => setInteracSettings({ ...interacSettings, checkout_instructions: e.target.value })}
                    placeholder="Please include your booking reference in the e-Transfer message. Your booking will be confirmed once we receive your payment."
                    rows={3}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none resize-none"
                    data-testid="interac-instructions-input"
                  />
                </div>
                
                <button
                  onClick={handleSaveInteracSettings}
                  disabled={savingInterac}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50"
                  data-testid="save-interac-btn"
                >
                  {savingInterac ? (
                    <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save Interac Settings
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Experience Package Deposit Settings */}
        <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)]">
          <div className="p-6 border-b border-[rgba(212,175,55,0.15)]">
            <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0] flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#D4AF37]" />
              Experience Package Deposits
            </h2>
          </div>
          
          <div className="p-6 space-y-6">
            <div className="bg-[#0F0F0F] p-4 border border-[rgba(212,175,55,0.1)]">
              <p className="text-[#A1A1AA] text-sm">
                Set the default deposit percentage required to secure an Experience Package booking. 
                Customers pay this percentage upfront, with the balance due after finalizing details.
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="form-label">Deposit Percentage</label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={depositSettings.deposit_percentage}
                    onChange={(e) => setDepositSettings({ 
                      ...depositSettings, 
                      deposit_percentage: Math.max(1, Math.min(100, parseInt(e.target.value) || 60))
                    })}
                    className="w-24 bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none text-center"
                    data-testid="deposit-percentage-input"
                  />
                  <span className="text-[#D4AF37] text-xl font-semibold">%</span>
                </div>
                <p className="text-[#a1a1aa] text-xs mt-2">
                  e.g., 60% means customer pays 60% now, 40% later
                </p>
              </div>
              
              <div className="flex items-center">
                <div className="bg-[#0F0F0F] p-4 border border-[rgba(212,175,55,0.2)] w-full">
                  <p className="text-[#A1A1AA] text-sm mb-1">Example: $1,000 package</p>
                  <p className="text-[#F5F5F0]">
                    Deposit: <span className="text-[#D4AF37] font-semibold">${(1000 * depositSettings.deposit_percentage / 100).toFixed(0)}</span>
                  </p>
                  <p className="text-[#F5F5F0]">
                    Balance: <span className="text-[#A1A1AA]">${(1000 - 1000 * depositSettings.deposit_percentage / 100).toFixed(0)}</span>
                  </p>
                </div>
              </div>
            </div>
            
            <button
              onClick={handleSaveDepositSettings}
              disabled={savingDeposit}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
              data-testid="save-deposit-btn"
            >
              {savingDeposit ? (
                <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Deposit Settings
            </button>
          </div>
        </div>
          </>
        )}

        {/* Email Templates Tab Content */}
        {activeTab === "emails" && (
          <div className="space-y-8">
            {/* Event Welcome Email */}
            <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)]">
              <div className="p-6 border-b border-[rgba(212,175,55,0.15)]">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0] flex items-center gap-2">
                    <Mail className="w-5 h-5 text-[#D4AF37]" />
                    Event Welcome Email
                  </h2>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={emailTemplates.event_welcome?.enabled ?? true}
                      onChange={(e) => updateTemplate("event_welcome", "enabled", e.target.checked)}
                      className="w-5 h-5 rounded border-[#D4AF37] bg-[#0F0F0F] text-[#D4AF37]"
                    />
                    <span className="text-[#A1A1AA] text-sm">Enabled</span>
                  </label>
                </div>
                <p className="text-[#A1A1AA] text-sm mt-2">
                  Sent automatically when a customer pays for event tickets via Stripe
                </p>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="bg-[#0F0F0F] p-4 border border-[rgba(212,175,55,0.1)]">
                  <p className="text-[#D4AF37] text-sm font-semibold mb-2">Available Placeholders:</p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {['{name}', '{event_title}', '{event_date}', '{event_time}', '{event_location}', '{ticket_type}', '{quantity}', '{booking_reference}', '{contact_email}'].map(p => (
                      <code key={p} className="px-2 py-1 bg-[#1a1a1a] text-[#A1A1AA] border border-[rgba(212,175,55,0.2)]">{p}</code>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="form-label">Email Subject</label>
                  <input
                    type="text"
                    value={emailTemplates.event_welcome?.subject || ""}
                    onChange={(e) => updateTemplate("event_welcome", "subject", e.target.value)}
                    placeholder="🎉 Your Tickets for {event_title} - See You There!"
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    data-testid="event-welcome-subject"
                  />
                </div>
                
                <div>
                  <label className="form-label">Email Body (HTML)</label>
                  <textarea
                    value={emailTemplates.event_welcome?.body || ""}
                    onChange={(e) => updateTemplate("event_welcome", "body", e.target.value)}
                    placeholder="<div>...</div>"
                    rows={15}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none font-mono text-sm resize-y"
                    data-testid="event-welcome-body"
                  />
                  <p className="text-[#a1a1aa] text-xs mt-2">
                    Use HTML for formatting. Placeholders will be replaced with actual values.
                  </p>
                </div>
                
                <button
                  onClick={() => handleSaveEmailTemplate("event_welcome")}
                  disabled={savingTemplate === "event_welcome"}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50"
                  data-testid="save-event-welcome-btn"
                >
                  {savingTemplate === "event_welcome" ? (
                    <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save Event Welcome Template
                </button>
              </div>
            </div>

            {/* Interac Instructions Email */}
            <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)]">
              <div className="p-6 border-b border-[rgba(212,175,55,0.15)]">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0] flex items-center gap-2">
                    <Banknote className="w-5 h-5 text-[#D4AF37]" />
                    Interac e-Transfer Instructions Email
                  </h2>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={emailTemplates.interac_instructions?.enabled ?? true}
                      onChange={(e) => updateTemplate("interac_instructions", "enabled", e.target.checked)}
                      className="w-5 h-5 rounded border-[#D4AF37] bg-[#0F0F0F] text-[#D4AF37]"
                    />
                    <span className="text-[#A1A1AA] text-sm">Enabled</span>
                  </label>
                </div>
                <p className="text-[#A1A1AA] text-sm mt-2">
                  Sent automatically when a customer chooses Interac e-Transfer at checkout
                </p>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="bg-[#0F0F0F] p-4 border border-[rgba(212,175,55,0.1)]">
                  <p className="text-[#D4AF37] text-sm font-semibold mb-2">Available Placeholders:</p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {['{name}', '{interac_email}', '{amount}', '{security_section}', '{booking_reference}', '{booking_description}', '{event_date}', '{contact_email}'].map(p => (
                      <code key={p} className="px-2 py-1 bg-[#1a1a1a] text-[#A1A1AA] border border-[rgba(212,175,55,0.2)]">{p}</code>
                    ))}
                  </div>
                  <p className="text-[#a1a1aa] text-xs mt-2">
                    Note: <code className="px-1 bg-[#1a1a1a]">{'{security_section}'}</code> auto-generates the security Q&A section if configured in Interac settings
                  </p>
                </div>
                
                <div>
                  <label className="form-label">Email Subject</label>
                  <input
                    type="text"
                    value={emailTemplates.interac_instructions?.subject || ""}
                    onChange={(e) => updateTemplate("interac_instructions", "subject", e.target.value)}
                    placeholder="📧 Interac e-Transfer Instructions - {booking_reference}"
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
                    data-testid="interac-instructions-subject"
                  />
                </div>
                
                <div>
                  <label className="form-label">Email Body (HTML)</label>
                  <textarea
                    value={emailTemplates.interac_instructions?.body || ""}
                    onChange={(e) => updateTemplate("interac_instructions", "body", e.target.value)}
                    placeholder="<div>...</div>"
                    rows={15}
                    className="w-full bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none font-mono text-sm resize-y"
                    data-testid="interac-instructions-body"
                  />
                  <p className="text-[#a1a1aa] text-xs mt-2">
                    Use HTML for formatting. Placeholders will be replaced with actual values.
                  </p>
                </div>
                
                <button
                  onClick={() => handleSaveEmailTemplate("interac_instructions")}
                  disabled={savingTemplate === "interac_instructions"}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50"
                  data-testid="save-interac-instructions-btn"
                >
                  {savingTemplate === "interac_instructions" ? (
                    <div className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save Interac Instructions Template
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add User Modal */}
        {showAddUserModal && (
          <AddUserModal
            onClose={() => setShowAddUserModal(false)}
            onSave={() => {
              setShowAddUserModal(false);
              fetchUsers();
            }}
          />
        )}

        {/* Change Password Modal */}
        {showPasswordModal && selectedUser && (
          <PasswordModal
            user={selectedUser}
            onClose={() => {
              setShowPasswordModal(false);
              setSelectedUser(null);
            }}
            onSave={() => {
              setShowPasswordModal(false);
              setSelectedUser(null);
              toast.success("Password updated successfully");
            }}
          />
        )}

        {/* Change Username Modal */}
        {showUsernameModal && selectedUser && (
          <UsernameModal
            user={selectedUser}
            onClose={() => {
              setShowUsernameModal(false);
              setSelectedUser(null);
            }}
            onSave={(newUsername) => {
              setShowUsernameModal(false);
              setSelectedUser(null);
              fetchUsers();
              // Update localStorage if current user
              if (selectedUser.id === currentUserId) {
                localStorage.setItem("adminUser", newUsername);
              }
              toast.success("Username updated successfully");
            }}
          />
        )}
      </div>
    </AdminLayout>
  );
};

// Add User Modal
const AddUserModal = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    role: "admin"
  });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setSaving(true);

    try {
      await axios.post(`${API}/admin/users`, {
        username: formData.username,
        password: formData.password,
        role: formData.role
      });
      toast.success("User created successfully");
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] w-full max-w-md"
      >
        <div className="p-6 border-b border-[rgba(212,175,55,0.15)] flex items-center justify-between">
          <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
            Add Admin User
          </h2>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-[#F5F5F0]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="form-label">Username *</label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
              placeholder="Enter username"
              required
              data-testid="new-username-input"
            />
          </div>

          <div>
            <label className="form-label">Password *</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 pr-10 focus:border-[#D4AF37] focus:outline-none"
                placeholder="Enter password"
                required
                minLength={6}
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a1a1aa] hover:text-[#D4AF37]"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="form-label">Confirm Password *</label>
            <input
              type={showPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
              placeholder="Confirm password"
              required
              data-testid="confirm-password-input"
            />
          </div>

          <div>
            <label className="form-label">Role</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
            >
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
            </select>
          </div>

          <div className="flex gap-4 pt-4 border-t border-[rgba(212,175,55,0.15)]">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
              data-testid="create-user-btn"
            >
              {saving ? (
                <>
                  <div className="w-5 h-5 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                "Create User"
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Change Password Modal
const PasswordModal = ({ user, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    if (formData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setSaving(true);

    try {
      await axios.put(`${API}/admin/users/${user.id}/password`, {
        current_password: formData.currentPassword,
        new_password: formData.newPassword
      });
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] w-full max-w-md"
      >
        <div className="p-6 border-b border-[rgba(212,175,55,0.15)] flex items-center justify-between">
          <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
            Change Password
          </h2>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-[#F5F5F0]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="flex items-center gap-3 p-3 bg-[#121212] border border-[rgba(212,175,55,0.1)]">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center">
              <span className="text-[#050505] font-bold">
                {user.username?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-[#F5F5F0]">{user.username}</p>
              <p className="text-[#a1a1aa] text-sm capitalize">{user.role}</p>
            </div>
          </div>

          <div>
            <label className="form-label">Current Password *</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={formData.currentPassword}
                onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 pr-10 focus:border-[#D4AF37] focus:outline-none"
                placeholder="Enter current password"
                required
                data-testid="current-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a1a1aa] hover:text-[#D4AF37]"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="form-label">New Password *</label>
            <input
              type={showPassword ? "text" : "password"}
              value={formData.newPassword}
              onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
              placeholder="Enter new password"
              required
              minLength={6}
              data-testid="new-password-input"
            />
          </div>

          <div>
            <label className="form-label">Confirm New Password *</label>
            <input
              type={showPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
              placeholder="Confirm new password"
              required
              data-testid="confirm-new-password-input"
            />
          </div>

          <div className="flex gap-4 pt-4 border-t border-[rgba(212,175,55,0.15)]">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
              data-testid="update-password-btn"
            >
              {saving ? (
                <>
                  <div className="w-5 h-5 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                  Updating...
                </>
              ) : (
                "Update Password"
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Change Username Modal
const UsernameModal = ({ user, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    newUsername: user.username,
    password: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.newUsername === user.username) {
      toast.error("Please enter a different username");
      return;
    }

    setSaving(true);

    try {
      await axios.put(`${API}/admin/users/${user.id}/username`, {
        new_username: formData.newUsername,
        password: formData.password
      });
      onSave(formData.newUsername);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update username");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.3)] w-full max-w-md"
      >
        <div className="p-6 border-b border-[rgba(212,175,55,0.15)] flex items-center justify-between">
          <h2 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0]">
            Change Username
          </h2>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-[#F5F5F0]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="form-label">New Username *</label>
            <input
              type="text"
              value={formData.newUsername}
              onChange={(e) => setFormData({ ...formData, newUsername: e.target.value })}
              className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 focus:border-[#D4AF37] focus:outline-none"
              placeholder="Enter new username"
              required
              data-testid="new-username-input"
            />
          </div>

          <div>
            <label className="form-label">Confirm with Password *</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 pr-10 focus:border-[#D4AF37] focus:outline-none"
                placeholder="Enter your password to confirm"
                required
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a1a1aa] hover:text-[#D4AF37]"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="flex gap-4 pt-4 border-t border-[rgba(212,175,55,0.15)]">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
              data-testid="update-username-btn"
            >
              {saving ? (
                <>
                  <div className="w-5 h-5 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                  Updating...
                </>
              ) : (
                "Update Username"
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AdminSettingsPage;
